import React from "react";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.45);
    z-index: 1300;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: ${fadeIn} 150ms ease;
`;

const Dialog = styled.div`
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
    width: 100%;
    max-width: 560px;
    animation: ${slideIn} 150ms ease;
    font-family: "Inter", system-ui, sans-serif;
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid #e2e8f0;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
`;

const Workspace = styled.div`
    margin-top: 2px;
    font-size: 0.78rem;
    color: #64748b;
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #94a3b8;
    font-size: 1.2rem;
    line-height: 1;
    padding: 4px;
    border-radius: 6px;
    &:hover { background: #f1f5f9; color: #0f172a; }
`;

const Body = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Footer = styled.div`
    padding: 16px 24px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
`;

const Btn = styled.button`
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: ${({ disabled }) => disabled ? "not-allowed" : "pointer"};
    opacity: ${({ disabled }) => disabled ? 0.5 : 1};
    transition: background 120ms;
    border: ${({ $primary }) => $primary ? "none" : "1px solid #e2e8f0"};
    background: ${({ $primary }) => $primary ? "#6366f1" : "#fff"};
    color: ${({ $primary }) => $primary ? "#fff" : "#374151"};
    &:hover:not(:disabled) {
        background: ${({ $primary }) => $primary ? "#4f46e5" : "#f8fafc"};
    }
`;

export const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const FieldLabel = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: #0f172a;
`;

// Something the user can put right themselves, rather than a failure: an amber note inside the
// dialog, not a red toast that reads as the app having broken.
export const WarningNote = styled.div`
    padding: 10px 12px;
    border: 1px solid #fde68a;
    border-radius: 8px;
    background: #fffbeb;
    font-size: 0.8rem;
    line-height: 1.45;
    color: #92400e;
`;

export const ErrorMsg = styled.div`
    font-size: 0.78rem;
    color: #ef4444;
    margin-top: 4px;
`;

// The dialog every connection in workspace settings is edited in, so Analytics and Search Console
// open the same thing: same chrome, same Cancel and Save, same close behaviour.
export const SettingsDialog = ({ title, workspace, isOpen, onClose, onSave, saveDisabled, children }) => {
    if (!isOpen) return null;

    return (
        <Overlay onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <Dialog role="dialog" aria-modal="true" aria-label={title}>
                <Header>
                    <div>
                        <Title>{title}</Title>
                        <Workspace>{[workspace.name, workspace.websiteUrl].filter(Boolean).join(" · ")}</Workspace>
                    </div>
                    <CloseBtn onClick={onClose} aria-label="Close">✕</CloseBtn>
                </Header>
                <Body>{children}</Body>
                <Footer>
                    <Btn onClick={onClose}>Cancel</Btn>
                    <Btn $primary disabled={saveDisabled} onClick={onSave}>Save</Btn>
                </Footer>
            </Dialog>
        </Overlay>
    );
};
